import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import type { OrgSettings, PushTarget } from '../../types';

const TARGET_OPTIONS: { value: PushTarget; label: string }[] = [
  { value: 'jira',     label: 'Jira'           },
  { value: 'ado',      label: 'Azure DevOps'   },
  { value: 'zephyr',   label: 'Zephyr Scale'   },
  { value: 'testlink', label: 'TestLink'         },
];

const EMPTY: OrgSettings = { targetType: 'jira' };

export default function PushTargetSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const { isAdmin }   = useUserRole();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [settings, setSettings] = useState<OrgSettings>(EMPTY);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    api.getSettings()
      .then(s => setSettings(s ?? EMPTY))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  function set<K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) {
    setSettings(s => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveSettings(settings);
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  const t = settings.targetType;

  return (
    <div>
      <Link to="/settings" className="settings-back-link">← Organisation Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">Push Target</h2>
          <p className="settings-section-desc">Choose where approved test cases are pushed. One target per organisation.</p>
          <div className="form-group">
            <label>Target System</label>
            <div className="target-selector">
              {TARGET_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`target-btn ${t === opt.value ? 'target-btn-active' : ''}`}
                  onClick={() => set('targetType', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {t === 'jira' && (
          <div className="settings-card">
            <h2 className="settings-section-title">Jira Configuration</h2>
            <div className="form-grid">
              <Field label="Jira Domain"  placeholder="yourorg.atlassian.net"  value={settings.jiraDomain      ?? ''} onChange={v => set('jiraDomain', v)} />
              <Field label="Jira Email"   placeholder="you@company.com"        value={settings.jiraEmail       ?? ''} onChange={v => set('jiraEmail', v)} />
              <Field label="API Token"    placeholder="Atlassian API token"    value={settings.jiraApiToken    ?? ''} onChange={v => set('jiraApiToken', v)}    secret />
              <Field label="Project Key"  placeholder="e.g. QA"               value={settings.jiraProjectKey  ?? ''} onChange={v => set('jiraProjectKey', v)} />
              <Field label="Issue Type"   placeholder="Test"                   value={settings.jiraIssueType   ?? 'Test'} onChange={v => set('jiraIssueType', v)} />
            </div>
          </div>
        )}

        {t === 'ado' && (
          <div className="settings-card">
            <h2 className="settings-section-title">Azure DevOps Configuration</h2>
            <div className="form-grid">
              <Field label="Organisation" placeholder="e.g. mycompany"        value={settings.adoOrg     ?? ''} onChange={v => set('adoOrg', v)} />
              <Field label="Project"      placeholder="e.g. MyProject"        value={settings.adoProject ?? ''} onChange={v => set('adoProject', v)} />
              <Field label="PAT Token"    placeholder="Personal Access Token"  value={settings.adoPat     ?? ''} onChange={v => set('adoPat', v)} secret />
            </div>
          </div>
        )}

        {t === 'zephyr' && (
          <div className="settings-card">
            <h2 className="settings-section-title">Zephyr Scale Configuration</h2>
            <div className="form-grid">
              <Field label="API Token"   placeholder="Zephyr Scale Bearer token" value={settings.zephyrToken      ?? ''} onChange={v => set('zephyrToken', v)} secret />
              <Field label="Project Key" placeholder="e.g. QA"                   value={settings.zephyrProjectKey ?? ''} onChange={v => set('zephyrProjectKey', v)} />
              <div className="form-group">
                <label>Data Region</label>
                <select
                  className="form-input"
                  value={settings.zephyrRegion ?? 'default'}
                  onChange={e => set('zephyrRegion', e.target.value)}
                >
                  <option value="default">Default (US)</option>
                  <option value="eu">EU</option>
                </select>
                <p className="hint-text" style={{ marginTop: '0.35rem' }}>
                  EU data-residency Jira sites use a separate Zephyr Scale API host — a token from
                  one region is rejected as "Unknown token" on the other. Check your Jira site's
                  data residency setting if unsure.
                </p>
              </div>
            </div>
          </div>
        )}

        {t === 'testlink' && (
          <div className="settings-card">
            <h2 className="settings-section-title">TestLink Configuration</h2>
            <p className="settings-section-desc">Shared across all users in your organisation.</p>
            <div className="form-grid">
              <Field label="TestLink URL"     placeholder="https://yourorg.testlink.org" value={settings.testLinkUrl         ?? ''} onChange={v => set('testLinkUrl', v)} />
              <Field label="API Key (devKey)" placeholder="TestLink developer key"       value={settings.testLinkApiKey      ?? ''} onChange={v => set('testLinkApiKey', v)} secret />
              <Field label="Project Name"     placeholder="e.g. QA Automation"          value={settings.testLinkProjectName ?? ''} onChange={v => set('testLinkProjectName', v)} />
            </div>
          </div>
        )}

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
